import type { ReactNode } from 'react';

import type { DocumentField } from '../api/types';

// Einfache Textformatierung für Dokumente und Hinweise – ohne HTML (sicher, React rendert nur Text):
//   # Titel · ## Abschnitt · ### Unterabschnitt · Absätze (Leerzeile) · Zeilenumbruch · **fett**
//   - Aufzählung · 1. Nummerierung · - [ ] / - [x] Ankreuzfeld · --- Linie
//   [[unterschrift: Auftraggeber | Makler]] · [[seitenumbruch]]
//   [[wenn feld.x]] … [[/wenn]] bzw. [[wenn feld.x = Wert]] … [[/wenn]] (mehrere Werte: [[wenn feld.x = Wert1|Wert2]])
//   Platzhalter {{lead.name}}, {{makler.firma}}, {{termin.datum}}, {{heute}}, {{feld.<schlüssel>}}

export const BLANK = '______________';

const placeholder = /\{\{\s*([a-z_][a-z0-9_]*(?:\.[a-z_][a-z0-9_]*)?)\s*\}\}/g;

// fieldText: Wert eines Feldes im Dokument (Auswahl → Baustein, Ankreuzfeld → x/Leerzeichen).
function fieldText(def: DocumentField | undefined, value: string): string {
  if (!def) return value;
  if (def.type === 'checkbox') return value === 'true' || value === 'ja' ? 'x' : ' ';
  if (def.type === 'select') {
    const option = def.options?.find((o) => o.value === value);
    return option?.text || value;
  }
  // Datum im Dokument deutsch (31.05.2031), gespeichert wird ISO (2031-05-31).
  const iso = def.type === 'date' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  return value;
}

// fillTemplate setzt Bedingungen und Platzhalter ein; leere Angaben werden zu Ausfülllinien (blank).
export function fillTemplate(
  body: string,
  context: Record<string, string>,
  defs: DocumentField[],
  values: Record<string, string>,
  blank = BLANK,
): string {
  const byKey = new Map(defs.map((d) => [d.key, d]));
  const lookup = (key: string, depth = 0): string => {
    if (key.startsWith('feld.')) {
      const k = key.slice(5);
      const text = fieldText(byKey.get(k), values[k] ?? '');
      // Bausteine dürfen selbst Platzhalter enthalten (eine Ebene).
      return depth === 0 ? text.replace(placeholder, (_, inner: string) => lookup(inner, 1) || blank) : text;
    }
    return context[key] ?? '';
  };
  const lines = body.split('\n');
  const out: string[] = [];
  const stack: boolean[] = [];
  for (const line of lines) {
    const cond = /^\s*\[\[wenn\s+([a-z0-9_.]+)\s*(?:=\s*(.+?))?\s*\]\]\s*$/.exec(line);
    if (cond) {
      const [, key, expected] = cond;
      const raw = key!.startsWith('feld.') ? (values[key!.slice(5)] ?? '') : (context[key!] ?? '');
      const truthy =
        expected === undefined ? raw.trim() !== '' && raw !== 'false' && raw !== 'nein' : expected.split('|').some((want) => raw.trim() === want.trim());
      stack.push(truthy);
      continue;
    }
    if (/^\s*\[\[\/wenn\]\]\s*$/.test(line)) {
      stack.pop();
      continue;
    }
    if (stack.includes(false)) continue;
    out.push(
      line.replace(placeholder, (_, key: string) => {
        const v = lookup(key);
        // Ankreuzfelder behalten ihr Leerzeichen („[ ]“), alles andere wird bei fehlendem Wert zur Linie.
        return v === ' ' ? v : v.trim() === '' ? blank : v;
      }),
    );
  }
  return out.join('\n');
}

function inline(text: string, key: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (p.startsWith('**') && p.endsWith('**') && p.length > 4 ? <strong key={`${key}-${i}`}>{p.slice(2, -2)}</strong> : p));
}

function withBreaks(lines: string[], key: string): ReactNode[] {
  return lines.flatMap((l, i) => (i === 0 ? inline(l, `${key}-${i}`) : [<br key={`${key}-br${i}`} />, ...inline(l, `${key}-${i}`)]));
}

// renderText wandelt formatierten Text in React-Elemente um.
export function renderText(text: string): ReactNode[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flushPara = () => {
    if (para.length) blocks.push(<p key={`p${blocks.length}`}>{withBreaks(para, `p${blocks.length}`)}</p>);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const k = `l${blocks.length}`;
    const items = list.items.map((item, i) => {
      const box = /^\[( |x|X)\]\s*(.*)$/.exec(item);
      if (box) {
        return (
          <li key={i} className="doc-check">
            <span className="doc-box" aria-label={box[1] === ' ' ? 'nicht angekreuzt' : 'angekreuzt'}>
              {box[1] === ' ' ? '☐' : '☒'}
            </span>{' '}
            {inline(box[2] ?? '', `${k}-${i}`)}
          </li>
        );
      }
      return <li key={i}>{inline(item, `${k}-${i}`)}</li>;
    });
    blocks.push(list.ordered ? <ol key={k}>{items}</ol> : <ul key={k}>{items}</ul>);
    list = null;
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const bullet = /^\s*-\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);
    const signature = /^\s*\[\[unterschrift:\s*(.*?)\]\]\s*$/.exec(line);
    if (line.trim() === '') {
      flushPara();
      flushList();
    } else if (heading) {
      flushPara();
      flushList();
      const level = heading[1]!.length;
      const rawHeading = heading[2] ?? '';
      const secMatch = level === 2 ? /^(\d+)\.\s+(.*)$/.exec(rawHeading) : null;
      if (secMatch) {
        const num = secMatch[1]!.padStart(2, '0');
        const titleContent = inline(secMatch[2] ?? '', `h${blocks.length}`);
        blocks.push(
          <h2 key={`h${blocks.length}`} className="doc-sec-heading">
            <span className="doc-sec-num">{num}</span>
            <span className="doc-sec-title">{titleContent}</span>
            <span className="doc-sec-rule" aria-hidden="true" />
          </h2>,
        );
      } else {
        const content = inline(rawHeading, `h${blocks.length}`);
        blocks.push(
          level === 1 ? (
            <h1 key={`h${blocks.length}`}>{content}</h1>
          ) : level === 2 ? (
            <h2 key={`h${blocks.length}`} className="doc-unnumbered-h2">
              <span>{content}</span>
              <i aria-hidden="true" />
            </h2>
          ) : (
            <h3 key={`h${blocks.length}`}>{content}</h3>
          ),
        );
      }
    } else if (signature) {
      flushPara();
      flushList();
      const labels = (signature[1] ?? '').split('|').map((s) => s.trim()).filter(Boolean);
      blocks.push(
        <div key={`s${blocks.length}`} className="doc-signatures">
          {labels.map((label) => (
            <div key={label} className="doc-signature">
              <div className="doc-signature-line" />
              <span>Ort, Datum, Unterschrift {label}</span>
            </div>
          ))}
        </div>,
      );
    } else if (/^\s*\[\[seitenumbruch\]\]\s*$/.test(line)) {
      flushPara();
      flushList();
      blocks.push(<div key={`b${blocks.length}`} className="doc-page-break" aria-hidden="true" />);
    } else if (/^\s*---\s*$/.test(line)) {
      flushPara();
      flushList();
      blocks.push(<hr key={`r${blocks.length}`} />);
    } else if (bullet || numbered) {
      flushPara();
      const ordered = !bullet;
      if (list && list.ordered !== ordered) flushList();
      list ??= { ordered, items: [] };
      list.items.push((bullet ?? numbered)![1] ?? '');
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return blocks;
}
